import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type DeviceStatus = 'ACTIVE' | 'PENDING_APPROVAL' | 'BLOCKED';

export interface Device {
  id: string;
  rustdeskId: string;
  alias: string | null;
  hostname: string | null;
  os: string | null;
  osVersion: string | null;
  rustdeskVersion: string | null;
  ip: string | null;
  online: boolean;
  lastSeenAt: string | null;
  status: DeviceStatus;
  tags: string[];
  notes: string | null;
  addressBookId: string | null;
  addressBook: { id: string; name: string } | null;
  groups: { groupId: string; group: { id: string; name: string } }[];
  createdAt: string;
}

interface PaginatedDevices {
  data: Device[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DeviceFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: DeviceStatus;
  online?: boolean;
  addressBookId?: string | 'unassigned';
  groupId?: string;
  tags?: string[];
  sortBy?: 'lastSeenAt' | 'alias' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export function useDevices(filters: DeviceFilters) {
  return useQuery({
    queryKey: ['devices', filters],
    queryFn: () =>
      api.get<PaginatedDevices>('/devices', {
        query: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 25,
          search: filters.search,
          status: filters.status,
          online: filters.online,
          addressBookId: filters.addressBookId,
          groupId: filters.groupId,
          tags: filters.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        },
      }),
    placeholderData: (prev) => prev,
  });
}

export interface DeviceStats {
  total: number;
  online: number;
  pending: number;
  blocked: number;
  byOs: Record<string, number>;
}

export function useDeviceStats() {
  return useQuery({
    queryKey: ['devices', 'stats'],
    queryFn: () => api.get<DeviceStats>('/devices/stats'),
    refetchInterval: 60_000,
  });
}

export interface UpdateDeviceInput {
  alias?: string | null;
  notes?: string | null;
  tags?: string[];
  addressBookId?: string | null;
  status?: DeviceStatus;
}

export function useUpdateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDeviceInput }) =>
      api.patch<Device>(`/devices/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });
}

export interface ImportCsvResult {
  imported: number;
  updated: number;
  failed: { row: number; reason: string; rustdeskId?: string }[];
}

export function useImportCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { csvContent: string; defaultAddressBookId?: string }) =>
      api.post<ImportCsvResult>('/devices/import-csv', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });
}
